import { writable, get } from 'svelte/store';
import type { NavigationStore, NavigationState } from '../types';
import { documentPageToUiIndex as docToUi, uiIndexToDocumentPage as uiToDoc } from '../utils/pageNumberUtils';

/**
 * Navigation Store 생성
 */
export const createNavigationStore = (): NavigationStore => {
    const { subscribe, set, update } = writable<NavigationState>({
        currentPage: 1, // 1-based UI index
        firstLoadedPage: 1,
        lastLoadedPage: 1,
        loadedPages: [],
    });

    return {
        subscribe,

        updateCurrentPage(pageNum: number): void {
            if (pageNum < 1) {
                console.warn('Invalid page number:', pageNum);
                return;
            }

            update(state => ({
                ...state,
                currentPage: pageNum,
            }));
        },

        uiIndexToDocumentPage(uiIndex: number): number | undefined {
            const state = get({ subscribe });
            return uiToDoc(uiIndex, state.loadedPages);
        },

        documentPageToUiIndex(docPage: number): number {
            const state = get({ subscribe });
            return docToUi(docPage, state.loadedPages);
        },

        goToPage(uiIndex: number): void {
            const state = get({ subscribe });
            const docPage = uiToDoc(uiIndex, state.loadedPages);

            if (!docPage) {
                console.warn('Page not loaded:', uiIndex);
                return;
            }

            update(s => ({
                ...s,
                currentPage: uiIndex,
            }));
        },

        addPages(pages: number[]): void {
            update(state => {
                // 기존 페이지와 새 페이지를 합치고 정렬
                const allPages = [...state.loadedPages, ...pages];
                const uniquePages = Array.from(new Set(allPages)).sort((a, b) => a - b);

                // firstLoadedPage와 lastLoadedPage 업데이트
                const firstLoadedPage = uniquePages.length > 0 ? uniquePages[0] : state.firstLoadedPage;
                const lastLoadedPage = uniquePages.length > 0 ? uniquePages[uniquePages.length - 1] : state.lastLoadedPage;

                return {
                    ...state,
                    loadedPages: uniquePages,
                    firstLoadedPage,
                    lastLoadedPage,
                };
            });
        },

        reset(): void {
            set({
                currentPage: 1,
                firstLoadedPage: 1,
                lastLoadedPage: 1,
                loadedPages: [],
            });
        },
    };
};
